/**
 * Лист платежа (16.10.2026, docs/design/pay-2026-10-16).
 *
 * Тап по платежу в «Платежах» открывал правку, в ленте на главной — сразу
 * «Отложить», а оплатить заранее было негде: владелец искал, в какой вкладке
 * записать оплату няне. Теперь тап везде открывает этот лист: сколько, когда
 * и сколько осталось; «Оплатить» — главная кнопка, «Отложить» — если под этот
 * раз ничего не отложено, правка — тихой строкой внизу.
 */
import { useState } from 'react';
import {
  ZERO, add, clampPos, formatMoney, scheduleLabel, sub,
  type Money, type PlainDate,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { ReserveSheet } from './home-parts.js';
import { SheetWrap } from './sheet.js';
import { stopObligation, topUpReserve, type AppState } from './store.js';
import { isSubscription } from './services.js';
import { flash } from './flash.js';
import { nextPaymentInstance } from './next-payment.js';
import { dayMonth, plural } from './text.js';

/** «23 октября, через неделю». */
function whenText(date: PlainDate, today: PlainDate): string {
  const n = Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
  const rel = n < 0 ? 'просрочен' : n === 0 ? 'сегодня' : n === 1 ? 'завтра' : n === 7 ? 'через неделю' : `через ${plural(n, 'день', 'дня', 'дней')}`;
  return `${dayMonth(date, today)}, ${rel}`;
}

export function PaymentSheet({ state, obligationId, date, second, onPay, onEdit, onClose, onChanged }: {
  state: AppState;
  obligationId: string;
  /** Какой раз: тот, по которому нажали в ленте. Нет — ближайший неоплаченный. */
  date?: PlainDate;
  /**
   * Вторая кнопка вместо «Отложить»: в ленте бывает «что делать, если не
   * хватает» и «Назначить из прихода». `null` — без второй кнопки.
   */
  second?: { label: string; run: () => void } | null;
  onPay: (target: { id: string; due: PlainDate; amount: Money }) => void;
  onEdit: () => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [reserving, setReserving] = useState(false);
  const o = state.obligations.find((x) => x.id === obligationId);
  const inst = nextPaymentInstance(state, obligationId, date);
  if (!o) return null;

  const account = state.accounts.find((a) => a.id === o.accountId);
  const rhythm = [
    o.schedule ? scheduleLabel(o.schedule) : `${o.dayOfMonth} числа`,
    ...(o.amountType === 'variable' ? ['сумма меняется'] : []),
    ...(account ? [`с «${account.name}»`] : []),
  ].join(' · ');

  /*
    «Отложить» — как в ленте: первым счётом тот, где уже лежит конверт под
    этот платёж, иначе счёт платежа. Свободно — остаток счёта без конвертов.
  */
  const freeOn = (id: string): Money => {
    const a = state.accounts.find((x) => x.id === id);
    if (!a) return ZERO;
    return clampPos(sub(a.balance, add(...state.reserves.filter((r) => r.accountId === id).map((r) => r.amount))));
  };
  const spendable = state.accounts.filter((a) => a.isLiquid && a.kind !== 'credit_card' && !a.archived)
    .map((a) => ({ id: a.id, name: a.name, free: freeOn(a.id) }));
  const held = state.reserves.filter((r) => r.forKind === 'obligation' && r.forId === o.id && r.amount > ZERO)
    .sort((a, b) => b.amount - a.amount)[0];
  const action = second === undefined
    ? (inst && inst.remaining > ZERO ? { label: `Отложить ${formatMoney(inst.remaining)}`, run: () => setReserving(true) } : null)
    : second;

  return (
    <>
      <SheetWrap title={o.name} onClose={onClose}>
        <div className="paysh-top">
          <b className="num">{formatMoney(inst?.amount ?? o.amount)}</b>
          <span>{rhythm}</span>
        </div>
        {inst ? (
          <div className="oprows paysh-rows">
            <div className="oprow"><span>{date ? 'Когда' : 'Ближайший'}</span><span className="opval plain">{whenText(inst.scheduledDate, state.today)}</span></div>
            <div className="oprow"><span>{inst.settled ? 'Оплачен' : 'Осталось оплатить'}</span>
              <span className="opval plain">{inst.settled ? 'целиком'
                : inst.paid > ZERO ? `${formatMoney(inst.unpaid)} из ${formatMoney(inst.amount)}` : formatMoney(inst.unpaid)}</span></div>
          </div>
        ) : (
          <div className="note">Платежей по расписанию больше нет.</div>
        )}
        <div className="form paysh-act">
          {inst && !inst.settled && (
            <button className="btn pri" onClick={() => onPay({ id: o.id, due: inst.scheduledDate, amount: inst.unpaid })}>Оплатить</button>
          )}
          {inst && !inst.settled && action && (
            <button className="btn" onClick={() => { if (second) onClose(); action.run(); }}>{action.label}</button>
          )}
          <button className="textact" onClick={onEdit}>Изменить платёж</button>
          {/* Подписку отменили — будущих списаний нет, прошлые остаются в истории (04.11.2026). */}
          {isSubscription(o) && (
            <button className="textact danger" onClick={async () => {
              await stopObligation(o.id);
              flash(`«${o.name}» больше не в платежах`);
              onChanged(); onClose();
            }}>Больше не плачу</button>
          )}
        </div>
      </SheetWrap>
      {reserving && inst && (
        <ReserveSheet
          target={{
            obligationId: o.id, label: o.name, date: inst.scheduledDate, need: inst.remaining,
            accountId: held?.accountId ?? o.accountId, accountHint: held ? 'здесь уже конверт' : 'счёт платежа',
          }}
          accounts={spendable} AmountField={AmountInput}
          onClose={() => setReserving(false)}
          onSave={async (accountId, amount) => {
            await topUpReserve({ accountId, forKind: 'obligation', forId: o.id, amount });
            setReserving(false);
            onClose();
            onChanged();
          }} />
      )}
    </>
  );
}
